import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  StorageLocation,
  CreateStorageLocationData,
  UpdateStorageLocationData,
  ReorderStorageLocationsData,
  MoveStorageLocationItemsData,
  MoveStorageLocationItemsResult,
} from '../types';

/**
 * Fetch all storage locations for a household.
 */
async function getStorageLocations(householdId: string): Promise<StorageLocation[]> {
  return apiFetch<StorageLocation[]>(`/households/${householdId}/storage-locations`);
}

/**
 * Fetch a single storage location.
 */
async function getStorageLocation(householdId: string, id: string): Promise<StorageLocation> {
  return apiFetch<StorageLocation>(`/households/${householdId}/storage-locations/${id}`);
}

/**
 * Create a new storage location.
 */
async function createStorageLocation(
  householdId: string,
  data: CreateStorageLocationData
): Promise<StorageLocation> {
  return apiFetch<StorageLocation>(`/households/${householdId}/storage-locations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing storage location.
 */
async function updateStorageLocation(
  householdId: string,
  id: string,
  data: UpdateStorageLocationData
): Promise<StorageLocation> {
  return apiFetch<StorageLocation>(`/households/${householdId}/storage-locations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a storage location.
 * The location must not have any items - use moveStorageLocationItems first.
 */
async function deleteStorageLocation(householdId: string, id: string): Promise<void> {
  await apiFetch(`/households/${householdId}/storage-locations/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Move all items from one storage location to another.
 */
async function moveStorageLocationItems(
  householdId: string,
  sourceId: string,
  data: MoveStorageLocationItemsData
): Promise<MoveStorageLocationItemsResult> {
  return apiFetch<MoveStorageLocationItemsResult>(
    `/households/${householdId}/storage-locations/${sourceId}/move-items`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Reorder storage locations.
 */
async function reorderStorageLocations(
  householdId: string,
  data: ReorderStorageLocationsData
): Promise<StorageLocation[]> {
  return apiFetch<StorageLocation[]>(`/households/${householdId}/storage-locations/reorder`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Hook to fetch all storage locations for a household.
 * Returns locations sorted by sortOrder.
 */
export function useStorageLocations(householdId: string) {
  return useQuery({
    queryKey: queryKeys.storageLocations.list(householdId),
    queryFn: () => getStorageLocations(householdId),
    enabled: !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single storage location.
 */
export function useStorageLocation(householdId: string, id: string) {
  return useQuery({
    queryKey: queryKeys.storageLocations.detail(householdId, id),
    queryFn: () => getStorageLocation(householdId, id),
    enabled: !!householdId && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to create a new storage location.
 * Invalidates the storage locations list for the household on success.
 */
export function useCreateStorageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, data }: { householdId: string; data: CreateStorageLocationData }) =>
      createStorageLocation(householdId, data),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storageLocations.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to update a storage location.
 * Updates the detail cache and invalidates the list.
 */
export function useUpdateStorageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      id,
      data,
    }: {
      householdId: string;
      id: string;
      data: UpdateStorageLocationData;
    }) => updateStorageLocation(householdId, id, data),
    onSuccess: (updatedLocation, { householdId, id }) => {
      queryClient.setQueryData(queryKeys.storageLocations.detail(householdId, id), updatedLocation);
      queryClient.invalidateQueries({
        queryKey: queryKeys.storageLocations.list(householdId),
      });
    },
  });
}

/**
 * Hook to delete a storage location.
 * The location must not have any items - use useMoveStorageLocationItems first.
 * Invalidates all storage location queries for the household on success.
 */
export function useDeleteStorageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, id }: { householdId: string; id: string }) =>
      deleteStorageLocation(householdId, id),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storageLocations.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to move all items from one storage location to another.
 * Use this before deleting a location that has items.
 * Invalidates both storage locations and items queries on success.
 */
export function useMoveStorageLocationItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      sourceId,
      destinationId,
    }: {
      householdId: string;
      sourceId: string;
      destinationId: string;
    }) =>
      moveStorageLocationItems(householdId, sourceId, {
        destinationLocationId: destinationId,
      }),
    onSuccess: (_, { householdId }) => {
      // Invalidate storage locations (item counts changed)
      queryClient.invalidateQueries({
        queryKey: queryKeys.storageLocations.byHousehold(householdId),
      });
      // Invalidate items (storage location changed)
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to reorder storage locations.
 * Provides optimistic updates for immediate visual feedback.
 * Invalidates the list on success to ensure consistency.
 */
export function useReorderStorageLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      data,
    }: {
      householdId: string;
      data: ReorderStorageLocationsData;
    }) => reorderStorageLocations(householdId, data),
    onMutate: async ({ householdId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.storageLocations.list(householdId),
      });

      // Snapshot the previous value
      const previousLocations = queryClient.getQueryData<StorageLocation[]>(
        queryKeys.storageLocations.list(householdId)
      );

      // Optimistically update the cache
      if (previousLocations) {
        const reorderedLocations = previousLocations
          .map((location) => {
            const reorderItem = data.items.find((item) => item.id === location.id);
            if (reorderItem) {
              return { ...location, sortOrder: reorderItem.sortOrder };
            }
            return location;
          })
          .sort((a, b) => a.sortOrder - b.sortOrder);

        queryClient.setQueryData(queryKeys.storageLocations.list(householdId), reorderedLocations);
      }

      return { previousLocations };
    },
    onError: (_err, { householdId }, context) => {
      // Rollback on error
      if (context?.previousLocations) {
        queryClient.setQueryData(
          queryKeys.storageLocations.list(householdId),
          context.previousLocations
        );
      }
    },
    onSettled: (_, __, { householdId }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: queryKeys.storageLocations.list(householdId),
      });
    },
  });
}
